import React, { createContext, useContext, useReducer, useState } from 'react';
import { RecordingRules, RoomType } from '../types';
import { TwilioError } from 'twilio-video';
import { settingsReducer, initialSettings, Settings, SettingsAction } from './settings/settingsReducer';
import useActiveSinkId from './useActiveSinkId/useActiveSinkId';
import useFirebaseAuth from './useFirebaseAuth/useFirebaseAuth';
import { useLocalStorageState } from '../hooks/useLocalStorageState/useLocalStorageState';
import usePasscodeAuth from './usePasscodeAuth/usePasscodeAuth';
import { User } from 'firebase/auth';
import useNameDomain from '../hooks/useNameDomain/useNameDomain';
import { API_UPDATE_VIDEO_RECORDINGRULES, API_VIDEO_TOKEN } from '../constants';

/**
 * The AppStateProvider component provides the application state to the rest of the application.
 * It uses the React Context API to make the state available to any child component that needs it.
 * Only the state which is common to the entire application should be stored here.
 * Avoid storing state that is only relevant to only Video or Voice App for example.
 * Instead store them in VideoProvider or VoiceProvider respectively.
 */

export interface StateContextType {
  error: TwilioError | Error | null;
  setError(error: TwilioError | Error | null): void;
  getToken(name: string, room: string, passcode?: string): Promise<string>;
  user?: User | null | { displayName: undefined; photoURL: undefined; passcode?: string };
  signIn?(passcode?: string): Promise<void>;
  signOut?(): Promise<void>;
  isAuthReady?: boolean;
  isFetching: boolean;
  activeSinkId: string;
  setActiveSinkId(sinkId: string): void;
  settings: Settings;
  dispatchSetting: React.Dispatch<SettingsAction>;
  roomType?: RoomType;
  updateRecordingRules(room_sid: string, rules: RecordingRules): Promise<object>;
  isGalleryViewActive: boolean;
  setIsGalleryViewActive: React.Dispatch<React.SetStateAction<boolean>>;
  maxGalleryViewParticipants: number;
  setMaxGalleryViewParticipants: React.Dispatch<React.SetStateAction<number>>;
  isKrispEnabled: boolean;
  setIsKrispEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isKrispInstalled: boolean;
  setIsKrispInstalled: React.Dispatch<React.SetStateAction<boolean>>;
  domain: string;
  username: string;
  isUsernameInUrl: boolean;
  roomName: string;
  setUsername: (val: string) => void;
  setRoomName: (val: string) => void;
  knockingUser: string;
  setKnockingUser: (val: string) => void;
  roomOwnerUsername: string;
  roomOwnerDomain: string;
  ownRoomDid: number;
  password: string;
  userToCall: string;
  userToInvite: string;
  meetingCreated: boolean;
  setMeetingCreated: React.Dispatch<React.SetStateAction<boolean>>;
}

export const StateContext = createContext<StateContextType>(null!);

/*
  The 'react-hooks/rules-of-hooks' linting rules prevent React Hooks from being called
  inside of if() statements. This is because hooks must always be called in the same order
  every time a component is rendered. The 'react-hooks/rules-of-hooks' rule is disabled below
  because the "if (process.env.REACT_APP_SET_AUTH === 'firebase')" statements are evaluated
  at build time (not runtime). If the statement evaluates to false, then the code is not
  included in the bundle that is produced (due to tree-shaking). Thus, in this instance, it
  is ok to call hooks inside if() statements.
*/
export default function AppStateProvider(props: React.PropsWithChildren<{}>) {
  const [error, setError] = useState<TwilioError | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [knockingUser, setKnockingUser] = useState('');
  const [isGalleryViewActive, setIsGalleryViewActive] = useLocalStorageState('gallery-view-active-key', true);
  const [activeSinkId, setActiveSinkId] = useActiveSinkId();
  const [settings, dispatchSetting] = useReducer(settingsReducer, initialSettings);
  const [maxGalleryViewParticipants, setMaxGalleryViewParticipants] = useLocalStorageState(
    'max-gallery-participants-key',
    6
  );

  const [isKrispEnabled, setIsKrispEnabled] = useState(false);
  const [isKrispInstalled, setIsKrispInstalled] = useState(false);

  const {
    domain,
    username,
    roomName,
    isUsernameInUrl,
    setUsername,
    setRoomName,
    roomOwnerUsername,
    roomOwnerDomain,
    ownRoomDid,
    password,
    userToCall,
    userToInvite,
    meetingCreated,
    setMeetingCreated,
  } = useNameDomain();

  let contextValue = {
    error,
    setError,
    isFetching,
    activeSinkId,
    setActiveSinkId,
    settings,
    dispatchSetting,
    isGalleryViewActive,
    setIsGalleryViewActive,
    maxGalleryViewParticipants,
    setMaxGalleryViewParticipants,
    isKrispEnabled,
    setIsKrispEnabled,
    isKrispInstalled,
    setIsKrispInstalled,
    domain,
    username,
    isUsernameInUrl,
    roomName,
    setUsername,
    setRoomName,
    knockingUser,
    setKnockingUser,
    roomOwnerUsername,
    roomOwnerDomain,
    ownRoomDid,
    password,
    userToCall,
    userToInvite,
    meetingCreated,
    setMeetingCreated,
  } as StateContextType;

  if (process.env.REACT_APP_SET_AUTH === 'firebase') {
    contextValue = {
      ...contextValue,
      ...useFirebaseAuth(), // eslint-disable-line react-hooks/rules-of-hooks
    };
  } else if (process.env.REACT_APP_SET_AUTH === 'passcode') {
    contextValue = {
      ...contextValue,
      ...usePasscodeAuth(), // eslint-disable-line react-hooks/rules-of-hooks
    };
  } else {
    contextValue = {
      ...contextValue,
      getToken: async (identity, roomname) => {
        const params = new window.URLSearchParams({ identity, roomname });
        const res = await fetch(`${API_VIDEO_TOKEN}?${params}`);
        const token = res.text();
        return token;
      },
      updateRecordingRules: async (room_sid, rules) => {
        return fetch(API_UPDATE_VIDEO_RECORDINGRULES, {
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ room_sid, rules }),
          method: 'POST',
        })
          .then(async res => {
            const jsonResponse = await res.json();

            if (!res.ok) {
              const recordingError = new Error(
                jsonResponse.error?.message || 'There was an error updating recording rules'
              );
              recordingError.code = jsonResponse.error?.code;
              return Promise.reject(recordingError);
            }

            return jsonResponse;
          })
          .catch(err => setError(err));
      },
    };
  }

  const getToken: StateContextType['getToken'] = async (name, room) => {
    setIsFetching(true);
    try {
      const res = await contextValue.getToken(name, room);
      setIsFetching(false);
      return res;
    } catch (err) {
      if (err instanceof TwilioError) setError(err);
      return Promise.reject(err);
    } finally {
      setIsFetching(false);
    }
  };

  const updateRecordingRules: StateContextType['updateRecordingRules'] = async (room_sid, rules) => {
    setIsFetching(true);
    try {
      const res = await contextValue.updateRecordingRules(room_sid, rules);
      return res;
    } catch (err) {
      setError(err as any);
      return Promise.reject(err);
    } finally {
      setIsFetching(false);
    }
  };

  return (
    <StateContext.Provider value={{ ...contextValue, getToken, updateRecordingRules }}>
      {props.children}
    </StateContext.Provider>
  );
}

export function useAppState() {
  const context = useContext(StateContext);
  if (!context) {
    throw new Error('useAppState must be used within the AppStateProvider');
  }
  return context;
}
